import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Calendar, Search, Mail, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/languageStore';
import { useWebsite } from '../contexts/websiteStore';
import { Link } from '../router/Link';

interface NewsItem {
    id: number;
    title: string;
    description: string;
    category: 'company' | 'industry' | 'press';
    date: string;
    image: string;
    readTime: string;
}

export function News() {
    const { t, isRTL } = useLanguage();
    const { config, getAsset } = useWebsite();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });

    // Sample news data
    const newsData: NewsItem[] = [
        {
            id: 1,
            title: t("news.articles.wasteManagement.title"),
            description: t("news.articles.wasteManagement.description"),
            category: "company" as const,
            date: "2025-08-14",
            image: getAsset(config.assets.news5 || 'news5.webp'),
            readTime: t("news.articles.wasteManagement.readTime")
        }
    ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()); // Sort by latest date first

    const categories = [
        { key: 'all', label: t('news.categories.all') },
        { key: 'company', label: t('news.categories.company') },
        { key: 'industry', label: t('news.categories.industry') },
        { key: 'press', label: t('news.categories.press') }
    ];

    const filteredNews = newsData.filter(item => {
        const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const handleSubscribe = () => {
        // TODO: Implement email subscription functionality
        console.log('Subscribe to updates');
    };

    return (
        <section className="relative overflow-hidden pt-24 pb-16">
            {/* Interactive gradient background */}
            <motion.div
                className="absolute inset-0"
                animate={{
                    background: [
                        `radial-gradient(circle at 50% 50%, var(--primary-color-rgb, 59, 130, 246) / 0.12 0%, transparent 40%)`,
                        `radial-gradient(circle at 50% 50%, var(--secondary-color-rgb, 34, 197, 94) / 0.12 0%, transparent 40%)`,
                        `radial-gradient(circle at 50% 50%, var(--primary-color-rgb, 59, 130, 246) / 0.12 0%, transparent 40%)`
                    ]
                }}
                transition={{
                    duration: 0.8,
                    ease: "easeOut"
                }}
            />

            {/* Enhanced background with vibrant gradients */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-primary/5 to-secondary/5"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-primary/3 via-transparent to-secondary/3"></div>

            {/* Animated background elements */}
            <motion.div
                className="absolute top-0 left-0 w-full h-full"
                animate={{
                    background: [
                        `radial-gradient(circle at 20% 80%, var(--primary-color-rgb, 59, 130, 246) / 0.08 0%, transparent 50%)`,
                        `radial-gradient(circle at 80% 20%, var(--secondary-color-rgb, 34, 197, 94) / 0.08 0%, transparent 50%)`,
                        `radial-gradient(circle at 20% 80%, var(--primary-color-rgb, 59, 130, 246) / 0.08 0%, transparent 50%)`
                    ]
                }}
                transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
            />

            <div className="container mx-auto px-6 relative z-10 max-w-7xl">
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    className="text-center mb-16"
                >
                    <motion.div
                        className="inline-flex items-center px-8 py-4 bg-primary/5 text-primary border border-primary/10 text-sm font-light tracking-wider mb-8 mt-10"
                        initial={{ opacity: 0, y: 20 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                        transition={{ delay: 0.2, duration: 0.8 }}
                    >
                        <span className="w-px h-4 bg-gray-800 mr-4"></span>
                        <span className="uppercase tracking-[0.2em]">{t("news.title")}</span>
                        <span className="w-px h-4 bg-gray-800 ml-4"></span>
                    </motion.div>
                    <motion.h2
                        className="text-4xl md:text-6xl font-light text-gray-900 mb-8 leading-tight"
                        initial={{ opacity: 0, y: 30 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                        transition={{ delay: 0.3, duration: 1.2, ease: "easeOut" }}
                    >
                        {t("news.heading.title")}
                    </motion.h2>
                    <motion.p
                        className="text-lg text-gray-600 max-w-2xl mx-auto tracking-wide"
                        initial={{ opacity: 0, y: 30 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                        transition={{ delay: 0.5, duration: 1.2, ease: 'easeOut' }}
                    >
                        {t("news.heading.subtitle")}
                    </motion.p>
                </motion.div>

                {/* Search and Filter */}
                <motion.div
                    className="flex flex-col md:flex-row gap-4 mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ delay: 0.6, duration: 0.8 }}
                >
                    <div className="relative flex-1">
                        <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4`} />
                        <input
                            type="text"
                            placeholder={t("news.search.placeholder")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 ${isRTL ? 'pr-10' : 'pl-10'}`}
                        />
                    </div>
                    <div className="flex gap-2">
                        {categories.map((category) => (
                            <button
                                key={category.key}
                                onClick={() => setSelectedCategory(category.key)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${selectedCategory === category.key
                                    ? 'bg-primary text-white shadow-md'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                {category.label}
                            </button>
                        ))}
                    </div>
                </motion.div>

                {/* News Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-10">
                    {filteredNews.map((item, index) => (
                        <motion.div
                            key={item.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            whileHover={{ y: -4 }}
                        >
                            <div className="h-full bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group overflow-hidden border border-gray-100">
                                <div className="relative overflow-hidden">
                                    <img
                                        src={item.image}
                                        alt={item.title}
                                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                                    />
                                    <div className="absolute top-4 left-4">
                                        <span className="px-3 py-1 bg-primary text-white text-xs font-medium rounded-full">
                                            {t(`news.categories.${item.category}`)}
                                        </span>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className={`flex items-center gap-2 text-sm text-gray-500 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                        <Calendar className="h-4 w-4" />
                                        <span>{new Date(item.date).toLocaleDateString()}</span>
                                        <span>•</span>
                                        <span>{item.readTime}</span>
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-primary transition-colors line-clamp-2">
                                        {item.title}
                                    </h3>
                                    <p className="text-gray-600 mb-4 line-clamp-3">
                                        {item.description}
                                    </p>
                                    <Link
                                        to={`/news/${item.id}`}
                                        className="inline-flex items-center text-primary hover:text-primary/80 font-medium transition-colors"
                                    >
                                        {t('news.readMore')}
                                        <ArrowRight className={`${isRTL ? 'mr-2 rotate-180' : 'ml-2'} h-4 w-4`} />
                                    </Link>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Subscribe Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="bg-primary rounded-2xl p-8 text-center text-white relative overflow-hidden"
                >
                    {/* Background pattern */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/20 to-transparent"></div>
                    </div>

                    <div className="relative z-10">
                        <h3 className="text-3xl md:text-4xl font-light mb-4">{t("news.subscribe.title")}</h3>
                        <p className="text-white/80 mb-6 max-w-md mx-auto">
                            {t("news.subscribe.description")}
                        </p>
                        <button
                            onClick={handleSubscribe}
                            className="inline-flex items-center px-6 py-3 bg-white text-primary hover:bg-primary/50 rounded-lg font-medium transition-all duration-300 shadow-sm"
                        >
                            <Mail className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                            {t('news.subscribe.button')}
                        </button>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
